import { Injectable } from '@angular/core';
import type { ProductPicker } from '@core-mkt/shared/components/layout/layout/layout';

@Injectable({
  providedIn: 'root',
})
export class ShortcodeDataContextService {
  private dataContext: Map<string, any> = new Map();

  /**
   * Set data for a specific key that can be used by shortcodes
   * @param key - The key to store the data under
   * @param data - The data to store
   */
  setData(key: string, data: any): void {
    this.dataContext.set(key, data);
  }

  /**
   * Get data for a specific key
   * @param key - The key to retrieve data for
   * @returns The data associated with the key, or null if not found
   */
  getData(key: string): any {
    return this.dataContext.get(key) || null;
  }

  /**
   * Set product picker data for use with [productPicker data="key"] shortcodes
   * @param key - The key to store the data under
   * @param productPickerData - The product picker data
   */
  setProductPickerData(key: string, productPickerData: ProductPicker[]): void {
    if (productPickerData.length) {
      this.setData(key, productPickerData);
    }
  }

  /**
   * Get product picker data for a specific key
   * @param key - The key to retrieve data for
   * @returns The product picker data or null if not found
   */
  getProductPickerData(key: string): ProductPicker[] | null {
    return this.getData(key);
  }

  /**
   * Clear all data from the context
   */
  clearAll(): void {
    this.dataContext.clear();
  }

  /**
   * Remove data for a specific key
   * @param key - The key to remove
   */
  removeData(key: string): void {
    this.dataContext.delete(key);
  }

  /**
   * Check if data exists for a key
   * @param key - The key to check
   * @returns True if data exists for the key, false otherwise
   */
  hasData(key: string): boolean {
    return this.dataContext.has(key);
  }
}